// apps/account-portal/src/stores/subscriptionStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCallback } from 'react';
import { subscriptionApi } from '../services/api';
import { SystemType, Plan } from '@redpanda/types';
import i18n from '../i18n';

/**
 * 401＝登入已失效（例如在其他系統登出），不是資料抓取失敗：不顯示錯誤，交給 UserContext 判定成登出
 * 重新整理時 isAuthenticated 先用 localStorage 的「上次已登入」，登入檢查還沒回來就會先打 API
 */
const isUnauthorized = (error: any) => error?.response?.status === 401;

export interface Subscription {
  id: string;
  system: SystemType;
  status: string;
  startDate: string;
  endDate: string;
  trialEndDate?: string;
  plan: {
    id: string;
    name: string;
    price: number;
    system: SystemType;
  };
}

interface SystemInvoice {
  id: string;
  /** 帳單編號 RP-年份-流水號 */
  invoiceNumber: string;
  date: string;
  amount: number;
  status: 'Paid' | 'Failed' | 'Pending';
  planName: string;
  system: SystemType;
  pdfUrl?: string;
}

interface SubscriptionState {
  // 所有系統訂閱資料
  subscriptions: Subscription[];
  subscriptionsLoaded: boolean;
  subscriptionsError: string | null;
  subscriptionsLastFetch: number;

  // 各系統的方案資料
  systemPlans: Record<SystemType, Plan[]>;
  systemPlansErrors: Record<SystemType, string | null>;
  systemPlansLastFetch: Record<SystemType, number>;

  // 帳單資料
  systemInvoices: SystemInvoice[];
  systemInvoicesLoaded: boolean;
  systemInvoicesError: string | null;
  systemInvoicesLastFetch: number;

  // 載入狀態
  isLoadingSubscriptions: boolean;
  isLoadingPlans: Record<SystemType, boolean>;
  isLoadingInvoices: boolean;

  // 操作狀態
  isCanceling: boolean;

  // Actions
  fetchAllSubscriptions: (forceRefresh?: boolean) => Promise<void>;
  fetchSubscription: (system: SystemType) => Promise<Subscription | null>;
  fetchSystemPlans: (system: SystemType) => Promise<void>;
  fetchSystemInvoices: () => Promise<void>;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  clearErrors: () => void;
  invalidateCache: () => void;
}

let pendingInvoices: Promise<void> | null = null;

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      // Initial state
      subscriptions: [],
      subscriptionsLoaded: false,
      subscriptionsError: null,
      subscriptionsLastFetch: 0,

      systemPlans: {} as Record<SystemType, Plan[]>,
      systemPlansErrors: {} as Record<SystemType, string | null>,
      systemPlansLastFetch: {} as Record<SystemType, number>,

      systemInvoices: [],
      systemInvoicesLoaded: false,
      systemInvoicesError: null,
      systemInvoicesLastFetch: 0,

      isLoadingSubscriptions: false,
      isLoadingPlans: {} as Record<SystemType, boolean>,
      isLoadingInvoices: false,

      isCanceling: false,

      // 獲取用戶所有系統訂閱
      fetchAllSubscriptions: async (forceRefresh = false) => {
        const state = get();
        const now = Date.now();

        // 如果有快取且非強制刷新，直接返回
        if (state.subscriptionsLoaded && !forceRefresh) {
          return;
        }

        set({
          isLoadingSubscriptions: true,
          subscriptionsError: null
        });

        try {
          const response = await subscriptionApi.get('/subscriptions');
          let subscriptions = response.data || [];


          set({
            subscriptions,
            subscriptionsLoaded: true,
            subscriptionsLastFetch: now,
            subscriptionsError: null,
          });
        } catch (error: any) {
          if (!isUnauthorized(error)) console.error('System subscriptions fetch error:', error);
          set({
            subscriptions: [],  // 寫入空快取，避免重複請求
            subscriptionsError: isUnauthorized(error) ? null : i18n.t('errors.business.fetchSubscriptionError'),
            subscriptionsLoaded: true,
            subscriptionsLastFetch: now,
          });
        } finally {
          set({ isLoadingSubscriptions: false });
        }
      },

      // 獲取特定系統訂閱
      fetchSubscription: async (system: SystemType) => {
        const state = get();

        // 若已批量載入過所有訂閱，直接回傳快取（含「無訂閱」的 undefined，不重複呼叫 API）
        if (state.subscriptionsLoaded) {
          return state.subscriptions.find(sub => sub.system === system) ?? null;
        }

        try {
          const response = await subscriptionApi.get(`/subscriptions/${system.toLowerCase()}`);
          const subscription = response.data;

          // 更新 store 中的資料
          set(state => ({
            subscriptions: [
              ...state.subscriptions.filter(sub => sub.system !== system),
              subscription
            ].filter(Boolean)
          }));

          return subscription;
        } catch (error: any) {
          if (error.response?.status === 404) {
            // 沒有訂閱是正常的
            return null;
          }
          console.error(`${system} subscription fetch error:`, error);
          throw error;
        }
      },

      // 獲取系統特定方案
      fetchSystemPlans: async (system: SystemType) => {
        const state = get();
        const now = Date.now();
        const CACHE_DURATION = 5 * 60 * 1000; // 5 分鐘快取

        // 快取有效時直接返回
        const lastFetch = state.systemPlansLastFetch[system];
        if (lastFetch && (now - lastFetch < CACHE_DURATION)) {
          return;
        }

        // 只有在沒有快取資料時才顯示 loading，否則靜默背景更新
        const hasData = (state.systemPlans[system]?.length ?? 0) > 0;
        if (!hasData) {
          set(state => ({
            isLoadingPlans: { ...state.isLoadingPlans, [system]: true },
            systemPlansErrors: { ...state.systemPlansErrors, [system]: null }
          }));
        }

        try {
          const response = await subscriptionApi.get(`/plans/${system.toLowerCase()}`);
          set(state => ({
            systemPlans: {
              ...state.systemPlans,
              [system]: response.data || []
            },
            systemPlansErrors: {
              ...state.systemPlansErrors,
              [system]: null
            },
            systemPlansLastFetch: {
              ...state.systemPlansLastFetch,
              [system]: now
            }
          }));
        } catch (error: any) {
          console.error(`${system} plans fetch error:`, error);
          set(state => ({
            systemPlans: {
              ...state.systemPlans,
              [system]: []  // 寫入空快取，避免重複請求
            },
            systemPlansErrors: {
              ...state.systemPlansErrors,
              [system]: i18n.t('errors.business.fetchPlansError')
            },
            systemPlansLastFetch: {
              ...state.systemPlansLastFetch,
              [system]: now
            }
          }));
        } finally {
          set(state => ({
            isLoadingPlans: { ...state.isLoadingPlans, [system]: false }
          }));
        }
      },

      // 獲取系統帳單
      // 先顯示快取、每次呼叫都背景重抓（R31）；同時只送一個請求
      // 原本「永久快取」：有快取就不再問伺服器，新付款、帳單編號等新欄位都看不到
      fetchSystemInvoices: () => {
        if (!pendingInvoices) {
          const now = Date.now();
          set({ isLoadingInvoices: !get().systemInvoicesLoaded, systemInvoicesError: null }); // 有快取時不顯示骨架
          pendingInvoices = subscriptionApi.get('/subscriptions/invoices')
            .then(response => set({
              systemInvoices: response.data,
              systemInvoicesLoaded: true,
              systemInvoicesLastFetch: now,
              systemInvoicesError: null,
            }))
            .catch((error: any) => {
              if (!isUnauthorized(error)) console.error('System invoices fetch error:', error);
              set({
                systemInvoicesError: isUnauthorized(error) ? null : i18n.t('errors.business.fetchBillingError'),
                systemInvoicesLoaded: true,
                systemInvoicesLastFetch: now,
              });
            })
            .finally(() => {
              set({ isLoadingInvoices: false });
              pendingInvoices = null;
            });
        }
        return pendingInvoices;
      },

      // 取消系統訂閱
      cancelSubscription: async (subscriptionId: string) => {
        set({ isCanceling: true, subscriptionsError: null });

        try {
          const response = await subscriptionApi.delete(`/subscriptions/${subscriptionId}`);
          const updatedSubscription = response.data;

          // 更新 store 中的訂閱狀態
          set(state => ({
            subscriptions: state.subscriptions.map(sub =>
              sub.id === subscriptionId ? updatedSubscription : sub
            )
          }));

        } catch (error: any) {
          console.error('Cancel subscription error:', error);
          set({
            subscriptionsError: i18n.t('errors.business.cancelSubscriptionError'),
          });
          throw error;
        } finally {
          set({ isCanceling: false });
        }
      },

      // 清除所有錯誤
      clearErrors: () => {
        set({
          subscriptionsError: null,
          systemPlansErrors: {} as Record<SystemType, string | null>,
          systemInvoicesError: null,
        });
      },

      // 使快取失效，強制重新獲取資料（僅用於結帳後等特殊情況）
      invalidateCache: () => {
        set({
          subscriptions: [],
          subscriptionsLoaded: false,
          subscriptionsLastFetch: 0,
          systemPlans: {} as Record<SystemType, Plan[]>,
          systemPlansLastFetch: {} as Record<SystemType, number>,
          systemInvoices: [],
          systemInvoicesLoaded: false,
          systemInvoicesLastFetch: 0,
        });
      },
    }),
    {
      name: 'system-subscription-store',
      // 只持久化資料和載入完成狀態，不持久化載入中狀態、錯誤和臨時狀態
      partialize: (state) => ({
        subscriptions: state.subscriptions,
        subscriptionsLoaded: state.subscriptionsLoaded,
        subscriptionsLastFetch: state.subscriptionsLastFetch,
        systemPlans: state.systemPlans,
        systemPlansLastFetch: state.systemPlansLastFetch,
        systemInvoices: state.systemInvoices,
        systemInvoicesLoaded: state.systemInvoicesLoaded,
        systemInvoicesLastFetch: state.systemInvoicesLastFetch,
        // 不持久化載入中狀態，確保恢復時總是 false
        // isLoadingSubscriptions: false (預設)
        // isLoadingPlans: {} (預設)
        // isLoadingInvoices: false (預設)
      }),
    }
  )
);

// 便利的 hook，用於獲取特定系統的訂閱資料
export const useSubscription = (system: SystemType) => {
  const store = useSubscriptionStore();

  const subscription = store.subscriptions?.find(sub => sub.system === system);
  const plans = store.systemPlans[system] || [];
  const isLoadingPlans = store.isLoadingPlans[system] || false;
  const plansError = store.systemPlansErrors[system] || null;

  // 使用 useCallback 避免函數重複創建導致重複呼叫
  // 方案頁要的是「所有系統的訂閱」：只有它會設 subscriptionsLoaded（訂閱資訊卡要等它才顯示）
  // 原本呼叫單一系統的 fetchSubscription，不會設這個旗標：直接打開 /crm（例如登入後導回）時訂閱卡一直不出現，要先去過首頁才有
  const fetchSubscriptions = useCallback(() => store.fetchAllSubscriptions(), []);
  const fetchSystemPlans = useCallback(() => store.fetchSystemPlans(system), [system]);

  return {
    subscription,
    plans,
    isLoadingPlans,
    plansError,
    subscriptionsLoaded: store.subscriptionsLoaded,
    subscriptionsError: store.subscriptionsError,
    isCanceling: store.isCanceling,
    fetchSubscriptions,
    fetchSystemPlans,
    cancelSubscription: store.cancelSubscription,
    clearErrors: store.clearErrors,
  };
};